import { ColumnDef } from "@tanstack/react-table";
import { format } from 'date-fns';
import { Badge, BadgeProps } from '@/components/ui/badge';
import { Button } from "@/components/ui/button";
import { MoreHorizontal, Edit, Trash, Calendar, User, FileText, DollarSign, Tag, MapPin } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SalesOrderListItem } from "@/app/types/salesOrder";
import { DataTableColumnHeader } from "@/components/ui/data-table-column-header";
import React from "react";

// Custom events for row actions
export const editSalesOrderEvent = 'edit-sales-order';
export const deleteSalesOrderEvent = 'delete-sales-order';

// Helper function to format currency
const formatCurrency = (amount: string | number): string => {
  const numAmount = Number(amount);
  if (isNaN(numAmount)) {
    return '₹0.00'; // Fallback for invalid amounts
  }
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
  }).format(numAmount);
};

// Helper function to format date
const formatDate = (dateString: string): string => {
  try {
    return format(new Date(dateString), 'dd/MM/yyyy');
  } catch (error) {
    return dateString;
  }
};

// Helper to get badge variant based on status
const getStatusBadgeVariant = (status: string): BadgeProps["variant"] => {
  switch (status.toLowerCase()) {
    case 'confirmed': return 'secondary';
    case 'in_progress': return 'secondary';
    case 'completed': return 'secondary';
    case 'draft': return 'outline';
    case 'cancelled': return 'destructive';
    default: return 'default';
  }
};

// Define custom meta type directly
interface CustomColumnMeta {
  label?: string;
  placeholder?: string;
  variant?: 'text' | 'number' | 'select' | 'date' | 'dateRange';
  icon?: React.ComponentType<{ className?: string }>;
  options?: Array<{ label: string; value: string | number | boolean }>;
}

// Use intersection type for ExtendedColumnDef
type ExtendedColumnDef<TData, TValue = unknown> = ColumnDef<TData, TValue> & {
  meta?: CustomColumnMeta;
  enableColumnFilter?: boolean;
};

export const columns: ExtendedColumnDef<SalesOrderListItem>[] = [
  {
    accessorKey: 'document_number',
    header: ({ column }) => <DataTableColumnHeader column={column} title="SO Number" />,
    cell: ({ row }): JSX.Element => <span className="font-medium">{row.getValue('document_number')}</span>,
    meta: {
      label: 'SO Number',
      placeholder: 'Filter SO numbers...',
      variant: 'text',
      icon: FileText,
    },
    enableColumnFilter: true,
  },
  {
    accessorKey: 'transaction_date',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />,
    cell: ({ row }): JSX.Element => {
      const date = row.getValue('transaction_date') as string;
      return <span>{date ? formatDate(date) : '-'}</span>;
    },
    meta: {
      label: 'Date',
      placeholder: 'Select date range...',
      variant: 'dateRange',
      icon: Calendar,
    },
    enableColumnFilter: true,
    enableSorting: true,
  },
  {
    id: 'party.name', 
    accessorKey: 'party.name',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Customer" />,
    cell: ({ row }): JSX.Element => {
      const party = row.original.party;
      return <span>{party?.name || '-'}</span>;
    },
    meta: {
      label: 'Customer',
      placeholder: 'Filter customers...',
      variant: 'text',
      icon: User,
    },
    enableColumnFilter: true,
  },
  {
    id: 'site.name',
    accessorKey: 'site.name',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Site" />,
    cell: ({ row }): JSX.Element => {
      const site = row.original.site;
      return <span>{site?.name || '-'}</span>;
    },
    meta: {
      label: 'Site',
      placeholder: 'Filter sites...',
      variant: 'text',
      icon: MapPin,
    },
    enableColumnFilter: true,
  },
  {
    accessorKey: 'total_amount',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Amount" />,
    cell: ({ row }): JSX.Element => {
      const amount = row.getValue('total_amount') as string;
      return <span className="font-medium text-right">{formatCurrency(amount)}</span>;
    },
    meta: {
      label: 'Amount',
      placeholder: 'Filter by amount...',
      variant: 'number',
      icon: DollarSign,
    },
    enableColumnFilter: true,
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
    cell: ({ row }): JSX.Element => {
      const status = row.getValue('status') as string;
      return (
        <Badge variant={getStatusBadgeVariant(status)}>
          {status.replace('_', ' ')}
        </Badge>
      );
    },
    meta: {
      label: 'Status',
      placeholder: 'Filter by status...',
      variant: 'select',
      icon: Tag,
      options: [
        { label: 'Draft', value: 'draft' },
        { label: 'Confirmed', value: 'confirmed' },
        { label: 'In Progress', value: 'in_progress' },
        { label: 'Completed', value: 'completed' },
        { label: 'Cancelled', value: 'cancelled' },
      ],
    },
    enableColumnFilter: true,
  },
  // Expected Delivery column - Hidden for accounting firm use
  // {
  //   accessorKey: 'expected_delivery_date',
  //   header: ({ column }) => <DataTableColumnHeader column={column} title="Expected Delivery" />,
  //   cell: ({ row }): JSX.Element => {
  //     const date = row.getValue('expected_delivery_date') as string | null;
  //     return <span>{date ? formatDate(date) : '-'}</span>;
  //   },
  //   meta: {
  //     label: 'Expected Delivery',
  //     placeholder: 'Select date range...',
  //     variant: 'dateRange',
  //     icon: Calendar,
  //   },
  //   enableColumnFilter: true,
  // },
  {
    id: "actions",
    cell: ({ row }): React.ReactNode => {
      const salesOrder = row.original;

      // Dispatch custom event for edit
      const handleEdit = (): void => {
        const event = new CustomEvent(editSalesOrderEvent, {
          detail: { salesOrder },
        });
        document.dispatchEvent(event);
      };

      // Dispatch custom event for delete
      const handleDelete = (): void => {
        const event = new CustomEvent(deleteSalesOrderEvent, {
          detail: { salesOrder },
        });
        document.dispatchEvent(event);
      };

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleEdit}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={handleDelete}
              className="text-destructive focus:text-destructive"
            >
              <Trash className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
]; 